import React from 'react';
import { PeopleProvider } from "./context/PeopleContext";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from './lib/api';
import EventDetail from './pages/EventDetail';
import { AuthProvider, useAuth } from './hooks/useAuth';
import './styles/globals.css';
import { ToastProvider } from './components/Toast';
import BiometricLock from './components/BiometricLock';

import LoginPage    from './pages/Login';
import AdminLayout  from './pages/AdminLayout';
import AdminToday   from './pages/AdminToday';
import SheetsList   from './pages/SheetsList';
import SheetBuilder from './pages/SheetBuilder';
import SheetLab from './pages/SheetLab';
import TravelPage from "./pages/Travel";
import PeoplePage from "./pages/People";
import EventsPage from "./pages/Events";
import SettingsPage from "./pages/Settings";
import DistributionPage from "./pages/Distribution";
import ApprovalsPage from "./pages/Approvals";
import BroadcastsPage from "./pages/Broadcasts";
import ViewerAccessPage from "./pages/ViewerAccess";
import AcceptInvitePage from "./pages/AcceptInvite";
import AccessCodePage from "./pages/AccessCode";
import OnboardingPage from "./pages/Onboarding";
// import SheetDetail from './pages/SheetDetail'; // kept for reference, redirected via SheetRedirect
import AgentReview  from './pages/AgentReview';
import ViewerPage   from './pages/Viewer';

// Redirect /admin/sheets/:id to /admin?sheet=:id
function SheetRedirect() {
  const { id } = useParams();
  const navigate = useNavigate();
  React.useEffect(() => {
    apiFetch('/sheets/' + id).then(data => {
      navigate(`/admin?date=${data.date}&artist=${data.artistId}`, { replace: true });
    }).catch(() => navigate('/admin', { replace: true }));
  }, [id, navigate]);
  return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}><div style={{ width:28, height:28, border:'2.5px solid var(--border)', borderTop:'2.5px solid var(--blue)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}></div></div>;
}

function RequireAuth({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text2)' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={user.role==='admin'?'/admin':'/viewer'} replace />;
  return children;
}

function AppRoutes() {
  const { user, biometricLocked, unlockBiometric, logout } = useAuth();
  if (user && biometricLocked) {
    return <BiometricLock onUnlock={unlockBiometric} onSignOut={logout} />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/access-code" element={<AccessCodePage />} />
      <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />
      <Route path="/auth/callback" element={<LoginPage />} />
      <Route path="/reset-password" element={<LoginPage />} />
      <Route path="/view/event/:sheetId/:eventId" element={<EventDetail />} />
      <Route path="/viewer" element={
        <RequireAuth>
          <ViewerPage />
        </RequireAuth>
      } />
      <Route path="/approvals" element={
        <RequireAuth>
          <ApprovalsPage />
        </RequireAuth>
      } />
      <Route path="/admin" element={
        <RequireAuth roles={['admin']}>
          <AdminLayout />
        </RequireAuth>
      }>
        <Route index element={<AdminToday />} />
        <Route path="sheets" element={<SheetsList />} />
        <Route path="sheets/lab" element={<SheetLab />} />
        <Route path="sheets/new" element={<SheetBuilder />} />
        <Route path="sheets/:id/review" element={<AgentReview />} />
        <Route path="sheets/:id" element={<SheetRedirect />} />
        <Route path="sheets/:id/edit" element={<SheetBuilder />} />
        <Route path="sheets/:id/event/:eventId" element={<EventDetail isAdmin />} />
        <Route path="artists" element={<PeoplePage artistsTab />} />
        <Route path="people"  element={<PeoplePage usersOnly />} />
        <Route path="invite"  element={<PeoplePage defaultInvite />} />
        <Route path="team"    element={<PeoplePage usersOnly />} />
        <Route path="users"   element={<PeoplePage usersOnly />} />
        <Route path="distribution" element={<DistributionPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="broadcasts" element={<BroadcastsPage />} />
        <Route path="viewer-access" element={<ViewerAccessPage />} />
        <Route path="travel"  element={<TravelPage />} />
        <Route path="events"  element={<EventsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="/" element={
        user
          ? <Navigate to={user.role==='admin'?'/admin':'/viewer'} replace />
          : <Navigate to="/login" replace />
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
      <PeopleProvider><BrowserRouter>
        <AppRoutes />
      </BrowserRouter></PeopleProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
